import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';
type OutlineEntry = {
  text: string;
  /** The first words that follow it. */
  preview: string;
};

// An output is a place in the page even though it carries no heading, so it opts in by attribute.
const LANDMARKS = 'h1, h2, h3, [data-outline]';

// An output names itself in these, so the observer watches attributes and not only text.
const LANDMARK_ATTRIBUTES = ['data-outline', 'data-outline-preview'];

// How far a heading settles under the top edge before the mark turns over — and, in `jumpTo`, how
// close to the top a landmark sits to count as the page's own title.
const REACHED_AT = 32;
const BREATHING_ROOM = 16;
const clean = (value: string | null | undefined) => (value || '').trim().replace(/\s+/g, ' ');
const labelOf = (element: HTMLElement) => clean(element.dataset.outline || element.textContent);
const previewOf = (element: HTMLElement) => {
  const own = clean(element.dataset.outlinePreview);
  if (own) return own;
  for (let next = element.nextElementSibling; next; next = next.nextElementSibling) {
    const text = clean(next.textContent);
    if (text) return text;
  }
  return '';
};

// The document is the only place that knows these headings — they are the author's own markdown. An
// untitled one, or one in sample output under `data-outline-skip`, is dropped here, at the element, so
// nothing downstream indexes a different list.
const landmarksIn = (root: HTMLElement) => Array.from(root.querySelectorAll<HTMLElement>(LANDMARKS)).filter(element => labelOf(element).length > 0 && !element.closest('[data-outline-skip]'));
const entriesIn = (root: HTMLElement): OutlineEntry[] => landmarksIn(root).map(element => ({
  text: labelOf(element),
  preview: previewOf(element)
}));

/**
 * Entries carry no element: a heading someone is typing into is re-rendered under React, so a stored
 * node would go stale. Position is resolved from the live document each time instead.
 */
const useOutline = (scrollRef, contentRef) => {
  const $ = _c(19);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [entries, setEntries] = useState(t0);
  const [activeIndex, setActiveIndex] = useState(0);
  let t1;
  let t2;
  if ($[1] !== contentRef || $[2] !== scrollRef) {
    t1 = () => {
      const scroller = scrollRef.current;
      if (!scroller) {
        return;
      }
      let rendered = "";
      let queued = 0;
      const sync = () => {
        queued = 0;
        const content = contentRef.current;
        if (!content) {
          return;
        }
        const next = entriesIn(content);
        const nextRendered = JSON.stringify(next);
        if (nextRendered === rendered) {
          return;
        }
        rendered = nextRendered;
        setEntries(next);
      };
      sync();
      const observer = new MutationObserver(() => {
        if (queued) {
          return;
        }
        queued = requestAnimationFrame(sync);
      });
      observer.observe(scroller, {
        attributeFilter: LANDMARK_ATTRIBUTES,
        characterData: true,
        childList: true,
        subtree: true
      });
      return () => {
        observer.disconnect();
        if (queued) {
          cancelAnimationFrame(queued);
        }
      };
    };
    t2 = [scrollRef, contentRef];
    $[1] = contentRef;
    $[2] = scrollRef;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== contentRef || $[6] !== scrollRef) {
    t3 = () => {
      const scroller_0 = scrollRef.current;
      if (!scroller_0) {
        return;
      }
      let queued_0 = 0;
      const track = () => {
        queued_0 = 0;
        const content_0 = contentRef.current;
        if (!content_0) {
          return;
        }
        const landmarks = landmarksIn(content_0);
        const scrolls = scroller_0.scrollHeight > scroller_0.clientHeight + 1;
        if (scrolls && scroller_0.scrollTop + scroller_0.clientHeight >= scroller_0.scrollHeight - 1) {
          setActiveIndex(Math.max(0, landmarks.length - 1));
          return;
        }
        const top = scroller_0.getBoundingClientRect().top + REACHED_AT;
        const reached = landmarks.filter(landmark => landmark.getBoundingClientRect().top <= top);
        setActiveIndex(Math.max(0, reached.length - 1));
      };
      track();
      const onScroll = () => {
        if (queued_0) {
          return;
        }
        queued_0 = requestAnimationFrame(track);
      };
      scroller_0.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => {
        scroller_0.removeEventListener("scroll", onScroll);
        if (queued_0) {
          cancelAnimationFrame(queued_0);
        }
      };
    };
    $[5] = contentRef;
    $[6] = scrollRef;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== contentRef || $[9] !== entries || $[10] !== scrollRef) {
    t4 = [scrollRef, contentRef, entries];
    $[8] = contentRef;
    $[9] = entries;
    $[10] = scrollRef;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== contentRef || $[13] !== scrollRef) {
    t5 = index => {
      const scroller_1 = scrollRef.current;
      const content_1 = contentRef.current;
      const landmark_0 = content_1 && landmarksIn(content_1)[index];
      if (!scroller_1 || !content_1 || !landmark_0) {
        return;
      }
      const box = landmark_0.getBoundingClientRect();
      const fromPageTop = box.top - content_1.getBoundingClientRect().top;
      const offset = box.top - scroller_1.getBoundingClientRect().top;
      const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      scroller_1.scrollTo({
        top: fromPageTop <= REACHED_AT ? 0 : Math.max(0, scroller_1.scrollTop + offset - BREATHING_ROOM),
        behavior: smooth ? "smooth" : "auto"
      });
    };
    $[12] = contentRef;
    $[13] = scrollRef;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const jumpTo = t5;
  let t6;
  if ($[15] !== activeIndex || $[16] !== entries || $[17] !== jumpTo) {
    t6 = {
      entries,
      activeIndex,
      jumpTo
    };
    $[15] = activeIndex;
    $[16] = entries;
    $[17] = jumpTo;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export type { OutlineEntry };
export default useOutline;